import stripeLogo from "@/assets/stripe-logo.webp";
import locationPin from "@/assets/location-pin.webp";
import verifiedBadge from "@/assets/verified-badge.webp";
import shieldIcon from "@/assets/shield-icon.webp";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";

const trustPillars = [
  {
    icon: null,
    image: stripeLogo,
    title: "Secure Payments by Stripe",
    description: "Industry-leading payment security with built-in deposits and fraud protection.",
    badge: "Powered by Stripe",
    gradient: "from-purple-500/20 via-pink-500/20 to-rose-500/20"
  },
  {
    icon: null,
    image: verifiedBadge,
    title: "Verified Users",
    description: "ID verification, profile reviews, and rating system for trusted transactions.",
    badge: "ID Verified",
    gradient: "from-purple-500/20 via-pink-500/20 to-rose-500/20"
  },
  {
    icon: null,
    image: locationPin,
    title: "Local Pickup",
    description: "Convenient in-person handoffs with address verification for safe exchanges.",
    badge: "Verified",
    gradient: "from-purple-500/20 via-pink-500/20 to-rose-500/20"
  },
  {
    icon: null,
    image: shieldIcon,
    title: "Damage Protection",
    description: "Optional coverage for peace of mind on every rental.",
    badge: "Protected",
    gradient: "from-purple-500/20 via-pink-500/20 to-rose-500/20"
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, rotateX: -15 },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1] as const
    }
  }
};

interface TrustCardProps {
  pillar: typeof trustPillars[0];
  index: number;
}

const TrustCard = ({ pillar, index }: TrustCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <motion.div 
      variants={itemVariants}
      whileHover={{ 
        scale: 1.03,
        rotateY: 5,
        z: 50
      }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      style={{ transformStyle: "preserve-3d" }}
    >
      <div
        ref={cardRef}
        className="relative group h-full"
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Animated border glow */}
        <motion.div 
          className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500"
          style={{
            background: `linear-gradient(135deg, hsl(var(--primary) / 0.3), hsl(var(--accent) / 0.2), hsl(var(--primary) / 0.3))`,
            backgroundSize: "200% 200%",
          }}
          animate={{
            backgroundPosition: isHovered ? ["0% 0%", "100% 100%"] : "0% 0%"
          }}
          transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
        />
        
        <Card 
          className="relative p-6 text-center bg-card border-border/40 rounded-2xl h-full overflow-hidden transition-all duration-300 group-hover:shadow-3d-hover"
        >
          {/* Radial glow following cursor */}
          <div 
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
            style={{
              background: `radial-gradient(circle at ${mousePos.x}% ${mousePos.y}%, hsl(var(--primary) / 0.1), transparent 50%)`
            }}
          />
          
          {/* Gradient background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${pillar.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
          
          <div className="relative z-10">
            {/* Icon with 3D effect */}
            <motion.div 
              className="mb-4 flex justify-center"
              whileHover={{ rotateY: 180 }}
              transition={{ duration: 0.6 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <motion.div 
                className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300"
                whileHover={{ 
                  scale: 1.1,
                  boxShadow: "0 10px 30px -10px hsl(var(--primary) / 0.4)"
                }}
              >
                {pillar.image ? (
                  <img src={pillar.image} alt={pillar.title} className={`object-contain ${pillar.title === "Damage Protection" ? "w-20 h-20" : "w-7 h-7"}`} />
                ) : pillar.icon ? (
                  <pillar.icon className="w-7 h-7 text-primary" />
                ) : null}
              </motion.div>
            </motion.div>
            
            {/* Animated badge */}
            <motion.span 
              className="inline-block px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-lg mb-3 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300"
              whileHover={{ scale: 1.05 }}
            >
              {pillar.badge}
            </motion.span>
            
            <h3 className="text-lg font-bold mb-2 text-foreground group-hover:text-primary transition-colors duration-300">
              {pillar.title}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {pillar.description}
            </p>
          </div>
        </Card>
      </div>
    </motion.div>
  );
};

const TrustSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const contentY = useTransform(scrollYProgress, [0, 1], [0, 28]);
  const opacity = useTransform(scrollYProgress, [0, 0.15, 0.55, 0.85], [0, 1, 1, 0]);

  return (
    <section ref={sectionRef} className="relative py-24 overflow-hidden bg-white">
      {/* Background orbs */}
      <div className="absolute top-40 right-20 w-72 h-72 rounded-full bg-primary/8 blur-3xl floating-orb" />
      <div className="absolute bottom-10 left-20 w-64 h-64 rounded-full bg-accent/8 blur-3xl floating-orb stagger-3" />
      
      <motion.div style={{ y: contentY, opacity }} className="container px-4 sm:px-6 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-slate-900">
            Why People Trust Rentify
          </h2>
        </motion.div>
        
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-50px" }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto"
          style={{ perspective: 1200 }}
        >
          {trustPillars.map((pillar, index) => (
            <TrustCard key={index} pillar={pillar} index={index} />
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default TrustSection;
